import { AlertTriangle, Home, RotateCcw } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui'
import { env, ROUTES } from '@/config'
import { cn } from '@/utils'

export interface ErrorStateProps {
  title?: string
  description?: string
  error?: Error
  /** Log correlation id shown to the user so support can find the entry. */
  reference?: string
  onRetry?: () => void
  showHomeLink?: boolean
  className?: string
}

export function ErrorState({
  title = 'Algo deu errado',
  description = 'Não foi possível concluir a operação. Tente novamente em instantes.',
  error,
  reference,
  onRetry,
  showHomeLink = true,
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed p-10 text-center',
        className,
      )}
    >
      <span className="bg-destructive/10 text-destructive flex size-12 items-center justify-center rounded-full">
        <AlertTriangle className="size-6" aria-hidden />
      </span>

      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-muted-foreground max-w-md text-sm">{description}</p>
      </div>

      {reference ? (
        <p className="text-muted-foreground font-mono text-xs">
          Referência: <span className="select-all">{reference}</span>
        </p>
      ) : null}

      {/* Diagnostics are development-only; production users see the reference. */}
      {!env.isProd && error ? (
        <pre className="bg-muted text-muted-foreground max-h-48 max-w-full overflow-auto rounded-lg p-3 text-left text-xs">
          {error.stack ?? error.message}
        </pre>
      ) : null}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {onRetry ? (
          <Button onClick={onRetry} variant="default">
            <RotateCcw aria-hidden />
            Tentar novamente
          </Button>
        ) : null}
        {showHomeLink ? (
          <Button asChild variant="outline">
            <Link to={ROUTES.dashboard}>
              <Home aria-hidden />
              Ir para o início
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  )
}
